import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty, isEqual } from 'lodash';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ProFormaCapTableModel } from 'api';
import { notFoundPath } from 'common/config/urls';
import { RELATED_ENTITY } from 'common/constants/companies';
import { CAPTABLE_VERSION_FILTER, CAPTABLE_VERSION_FILTER_TITLE } from 'common/constants/pageFilters';
import { useStore } from 'common/store';
import { CapTableVersion } from 'common/types/company';
import { SetPageFilters, UseStoreValue } from 'common/types/store';
import LayoutContext from 'context/LayoutContext';
import UnsavedChangesContext from 'context/UnsavedChangesContext';
import { IMeasurementDate } from 'models/interfaces/measurement-date.interface';
import { useCreateCapTableProforma, useGetCaptableInfo } from 'services/hooks/captable';
import { useGetVersionsByCompanyIdByDateId } from 'services/hooks/company';
import useGenerateChildrenCompaniesCapTablesVersions from './useGenerateChildrenCompaniesCapTablesVersions';
import { RelatedEntity } from '../../../ValuationsAllocation/types';

const useCapTableVersionFilter = ({
  selectedMeasurementDate,
  onAddRelatedEntity,
}: {
  selectedMeasurementDate: IMeasurementDate;
  onAddRelatedEntity: () => void;
}) => {
  const { tableSlugParam } = useParams<{ tableSlugParam: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const parsedQuery = queryString.parse(location.search);

  const [{ fundList, firmId, companyInfo, captableInfo, companyList }] = useStore() as unknown as UseStoreValue[];
  const companyId = companyInfo?.id;
  const selectedMeasurementDateId = selectedMeasurementDate?.id;
  const selectedDate = selectedMeasurementDate?.date;

  const { pageFilters, setPageFilters } = useContext(LayoutContext);

  const { action: hasChanges, showDialog } = useContext(UnsavedChangesContext);

  const getCapTableVersions = useGetVersionsByCompanyIdByDateId();
  const [capTableVersions, setCapTableVersions] = useState<CapTableVersion[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<string | number | null>();
  const [newProforma, setProformaInfo, createProforma] = useCreateCapTableProforma() as unknown as [
    ProFormaCapTableModel & { slug: string },
    (payload: ProFormaCapTableModel) => void,
    (payload: ProFormaCapTableModel) => void,
  ];
  const [capTableVersionsByCompany, setCapTableVersionsByCompany] = useState<Record<string, CapTableVersion[]>>({});

  const [, getCapTableInfo] = useGetCaptableInfo() as unknown as [
    (id: number) => Promise<void>,
    (id: number) => Promise<void>,
  ];

  const { generateChildrenCompaniesCapTablesVersions } = useGenerateChildrenCompaniesCapTablesVersions(
    companyList,
    selectedDate,
    companyId
  );
  const fetchCapTableVersions = useCallback(async () => {
    const fetchedCapTableVersions = (await getCapTableVersions(
      companyId,
      selectedMeasurementDateId,
      true
    )) as (CapTableVersion & { id: number })[];
    const sortedCapTableVersions = fetchedCapTableVersions?.toSorted((a, b) => a.id - b.id);

    let tempCapTableVersionsByCompany: Record<string, CapTableVersion[]> = {
      [companyInfo!.name]: sortedCapTableVersions,
    };

    if (companyInfo && companyInfo?.child_companies && companyInfo?.child_companies?.length > 0) {
      const childCompanyCapTableVersions = await generateChildrenCompaniesCapTablesVersions();
      tempCapTableVersionsByCompany = { ...tempCapTableVersionsByCompany, ...childCompanyCapTableVersions };
    }
    setCapTableVersionsByCompany(tempCapTableVersionsByCompany);

    setCapTableVersions(sortedCapTableVersions);
  }, [
    companyId,
    selectedMeasurementDateId,
    getCapTableVersions,
    generateChildrenCompaniesCapTablesVersions,
    companyInfo,
  ]);

  const setQueryString = useCallback(
    (paramName: string, newQueryString: string, replace = true) => {
      const currentLocation = window.location;
      const parsed = queryString.parse(currentLocation.search);
      parsed[paramName] = newQueryString;

      const newLocation = {
        pathname: currentLocation.pathname,
        search: queryString.stringify(parsed),
      };

      return replace ? navigate(newLocation, { replace: true }) : navigate(newLocation);
    },
    [navigate]
  );

  const handleVersionQueryString = async (
    versionArg: string,
    capTableVersionsArg: CapTableVersion[] = capTableVersions
  ) => {
    const { version: versionQueryString } = parsedQuery;
    const version = versionArg || versionQueryString;
    const { [companyInfo!.name]: primaryCompanyVersions, ...childCompanyMapping } = capTableVersionsByCompany;

    const selectPrimaryCapTable = async () => {
      try {
        setQueryString('version', companyInfo!.cap_table_slug as string);
        await handleVersionQueryString(companyInfo!.cap_table_slug!, capTableVersionsArg);
      } catch (error: any) {
        throw new Error(error);
      }
    };

    const matchVersion = (version: string) =>
      capTableVersionsArg.find(item => item.slug === version || item?.id?.toString() === version.toString());
    try {
      // this is where we need to find out that the version is a child company version
      if (version) {
        const matchedVersion = matchVersion(version as string);

        if (matchedVersion) {
          if (versionQueryString !== matchedVersion.slug) {
            setQueryString('version', matchedVersion.slug as string);
          }
          if (selectedVersion?.toString() !== matchedVersion?.id?.toString()) {
            setSelectedVersion(matchedVersion.id);
          }

          await getCapTableInfo(matchedVersion.id as number);
        } else {
          // Find the childCompany that has this version
          const entries = Object.entries(childCompanyMapping);

          const { relatedEntity, version: versionFromChildCompany } = entries.reduce(
            (acc, [company, versions]) => {
              const foundVersion = versions.find(v => v.slug === version || v?.id?.toString() === version.toString());
              if (foundVersion) {
                const companyDetails = (companyInfo?.child_companies as RelatedEntity[])?.find(
                  c => c.company_name === company
                );
                return { relatedEntity: companyDetails!, version: foundVersion };
              }
              return acc;
            },
            { relatedEntity: null, version: null } as {
              relatedEntity: RelatedEntity | null;
              version: CapTableVersion | null;
            }
          );

          if (versionFromChildCompany) {
            await getCapTableInfo(versionFromChildCompany.id as number);
            const { measurement_date_slug } = relatedEntity as RelatedEntity;

            setQueryString('date', measurement_date_slug);
            setQueryString('version', versionFromChildCompany.slug as string);

            if (selectedVersion?.toString() !== versionFromChildCompany?.id?.toString()) {
              setSelectedVersion(versionFromChildCompany.id);
            }

            return;
          }

          navigate(notFoundPath);
        }
      } else if (companyInfo?.firm_id === firmId && companyInfo?.cap_table_slug) {
        await selectPrimaryCapTable();
      }
      setProformaInfo({} as ProFormaCapTableModel);
    } catch (error: any) {
      throw new Error(error);
    }
  };

  useEffect(() => {
    if (!isEmpty(capTableVersions)) {
      const primaryCaptable = capTableVersions.find(ct => ct.is_primary);
      const isValidSelectedVersion = !!capTableVersions.find(ct => ct?.id?.toString() === selectedVersion?.toString());
      if (!isValidSelectedVersion) {
        setSelectedVersion(null);
      }

      const version = isValidSelectedVersion
        ? selectedVersion
        : newProforma?.id || primaryCaptable?.slug || parsedQuery?.version || null;

      handleVersionQueryString(version as string, capTableVersions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capTableVersions, tableSlugParam]);

  const saveAsNewProforma = useCallback(
    (payload: ProFormaCapTableModel) => {
      createProforma(payload);
    },
    [createProforma]
  );

  useEffect(() => {
    const hasRequiredData = !isEmpty(companyInfo) && !isEmpty(capTableVersionsByCompany);
    const relatedEntityAdded =
      companyInfo?.child_companies?.length !== Object.keys(capTableVersionsByCompany).length - 1;
    const shouldUpdateCapTableVersions = hasRequiredData && relatedEntityAdded;

    if (shouldUpdateCapTableVersions) {
      fetchCapTableVersions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyInfo]);

  // get cap table versions when the selected measurement date changes
  useEffect(() => {
    if (selectedMeasurementDate) {
      fetchCapTableVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeasurementDate]);

  const updateCapTableVersions = useCallback(
    async (proformaId: number) => {
      if (proformaId && companyInfo && selectedMeasurementDate) {
        // Update Cap Table versions
        await fetchCapTableVersions();

        setSelectedVersion(proformaId);
        setQueryString('version', newProforma.slug, false);
      }
    },
    [setQueryString, companyInfo, selectedMeasurementDate, newProforma.slug, fetchCapTableVersions]
  );

  useEffect(() => {
    if (!isEmpty(newProforma)) {
      updateCapTableVersions(newProforma.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newProforma]);

  const versionName = useMemo(() => {
    if (isEmpty(capTableVersions) || !selectedVersion) {
      return '';
    }
    const result = capTableVersions?.find(option => option?.id?.toString() === selectedVersion?.toString());

    return isEmpty(result) ? '' : result.name;
  }, [capTableVersions, selectedVersion]);

  const onVersionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const callback = async () => {
      try {
        await handleVersionQueryString(event.target.value);
      } catch (error: any) {
        throw new Error(error);
      }
    };

    if (hasChanges) {
      showDialog(callback);
    } else {
      callback();
    }
  };

  const onEditCaptable = async () => {
    if (!isEmpty(captableInfo)) {
      await fetchCapTableVersions();
    }
  };

  /**
   * Combines each company and its cap table versions into a single array of options where the company name is a disabled option
   * and the cap table versions are sub options
   * @returns {Array} Array of options for both companies and their cap table versions
   */
  const generateCapTableVersionSelectOptions = useCallback(() => {
    const selectOptions: (CapTableVersion & { id?: string | number; isDisabled?: boolean })[] = [];

    Object.entries(capTableVersionsByCompany)
      .filter(([_, tableVersions]) => tableVersions.length > 0)
      .sort(([aCompanyName, _], [_bCompanyName, _bTableVersions]) =>
        // sort the current company to the top of the list
        aCompanyName === companyInfo?.name ? -1 : 1
      )
      .forEach(([companyName, tableVersions]) => {
        selectOptions.push(
          {
            name: companyName,
            id: companyName as unknown as number,
            isDisabled: true,
          },
          ...tableVersions.map(ct => ({
            ...ct,
            isSubOption: true,
          }))
        );
      });
    return selectOptions;
  }, [capTableVersionsByCompany, companyInfo]);

  const setFilters = () => {
    const pageFilterMap = pageFilters
      ? pageFilters.reduce(
        (sofar, pageFilter) => {
          // eslint-disable-next-line no-param-reassign
          sofar[pageFilter.id!] = pageFilter;
          return sofar;
        },
          {} as Record<string, SetPageFilters>
      )
      : {};

    const selectOptions = generateCapTableVersionSelectOptions();

    if (
      pageFilterMap[CAPTABLE_VERSION_FILTER]?.selectedValue !== selectedVersion ||
      !isEqual(selectOptions, pageFilterMap[CAPTABLE_VERSION_FILTER]?.options)
    ) {
      const foundVersion = Object.values(capTableVersionsByCompany)
        .flatMap(versions => versions)
        .find(version => version?.id?.toString() === selectedVersion?.toString());

      const selectedValue = foundVersion?.id || capTableVersions.find(ct => ct.is_primary)?.id;

      pageFilterMap[CAPTABLE_VERSION_FILTER] = {
        id: CAPTABLE_VERSION_FILTER,
        options: selectOptions,
        title: CAPTABLE_VERSION_FILTER_TITLE,
        selectedValue,
        handler: onVersionChange,
        addOption: onAddRelatedEntity,
        addOptionLabel: RELATED_ENTITY.ADD_ACTION,
      };

      setPageFilters?.(Object.values(pageFilterMap));
    }
  };

  // Set filters
  useEffect(() => {
    if (!isEmpty(fundList) && !isEmpty(capTableVersions) && selectedVersion && selectedVersion !== '' && pageFilters) {
      setFilters();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageFilters, selectedMeasurementDate, selectedVersion, fundList, capTableVersions]);

  return {
    saveAsNewProforma,
    versionName,
    selectedVersion,
    onEditCaptable,
    updateCapTableVersions,
    capTableVersionsByCompany,
  };
};

export default useCapTableVersionFilter;
